import type { ReactNode } from 'react';
import { Card, CardHeader, CardTitle } from '@paige/ui';
import { useFormat } from '@paige/format';
import type { AccountDevice } from './account-types';
import { DeviceDiagnostics } from './DeviceDiagnostics';
import { SHOWN_TO_SUPPORT } from '../words';
import './insights.css';
import './account.css';

/**
 * A device that watches folders and has not been heard from in this long is quiet: the same 15 minutes
 * as the device notices and paige-watch (DeviceHealthService.Quiet, docs/engineering/folder-monitor.md),
 * so this panel, the company's own Devices page and the notice agree. A copier panel or an attended
 * scanner with no folders is never "quiet": it is only used when someone scans.
 */
const QUIET_MS = 15 * 60_000;

function seenWord(
  d: AccountDevice,
  ago: (iso: string) => string,
): { text: string; quiet: boolean } {
  if (!d.lastSeenAt) return { text: 'never heard from', quiet: true };
  const quiet = d.folders > 0 && Date.now() - Date.parse(d.lastSeenAt) > QUIET_MS;
  return { text: `${quiet ? 'not heard from since' : 'seen'} ${ago(d.lastSeenAt)}`, quiet };
}

/**
 * The company's connected computers and copiers (round 2, support S1; migrations 0131 to 0138): what
 * each is, its version, when it was last heard from, its watched folders and their problems, and
 * its last import. The first question on a "my scans stopped" call. Under each, folded, what its
 * folders sent, its last events and the logs it sent to Ademero support (0147); `logHref` links a log
 * for the staff roles that may download it. Paige Scan has one update channel, Stable (Dustin,
 * 2026-10-05), so the card no longer shows or changes one.
 */
export function DevicesPanel({
  devices,
  logHref,
}: {
  /** Null: the caller's staff role does not read devices. */
  devices: readonly AccountDevice[] | null;
  logHref?: ((deviceId: string, logId: string) => string) | undefined;
}): ReactNode {
  return (
    <Card id="ad-devices">
      <CardHeader>
        <CardTitle>Devices</CardTitle>
      </CardHeader>
      {devices === null ? (
        <p className="ad-small pui-muted">{SHOWN_TO_SUPPORT}</p>
      ) : devices.length === 0 ? (
        <p className="ad-small">
          No Paige Scan computer or copier panel connected: pages come by email, upload or the API.
        </p>
      ) : (
        <ul className="ad-devices">
          {devices.map((d) => (
            <DeviceRow key={d.id} d={d} logHref={logHref} />
          ))}
        </ul>
      )}
    </Card>
  );
}

const whatWord = (d: AccountDevice) =>
  [
    d.kind === 'copier' ? 'copier panel' : 'computer',
    d.model,
    // The model often carries the version already ("Paige Scan 1.5.0"): say it once.
    d.version && !(d.model ?? '').includes(d.version)
      ? `v${d.version}`
      : d.version
        ? null
        : 'version unknown',
  ]
    .filter(Boolean)
    .join(' · ');

function DeviceRow({
  d,
  logHref,
}: {
  d: AccountDevice;
  logHref: ((deviceId: string, logId: string) => string) | undefined;
}): ReactNode {
  const f = useFormat();
  const seen = seenWord(d, (iso) => f.ago(iso));
  return (
    <li className={seen.quiet ? 'is-quiet' : undefined}>
      <strong>
        <span aria-hidden="true">{d.kind === 'copier' ? '⎙' : '▣'}</span> {d.name}
      </strong>
      <span>{whatWord(d)}</span>
      <span className={seen.quiet ? 'ad-tone-warning' : undefined}>
        {seen.quiet ? <span aria-hidden="true">⚠ </span> : null}
        {seen.text}
      </span>
      <span>
        {d.folders} watched folder{d.folders === 1 ? '' : 's'}
        {d.foldersWithProblems ? `, ${d.foldersWithProblems} with a problem` : ''}
        {d.lastImportAt ? ` · last import ${f.ago(d.lastImportAt)}` : ' · no import yet'}
      </span>
      <DeviceDiagnostics d={d} logHref={logHref ? (logId) => logHref(d.id, logId) : undefined} />
    </li>
  );
}
